import { ReactElement, useEffect, useState, useMemo } from 'react'
import { GetStaticPropsContext } from 'next'
import retriveMessages, { LocaleType } from '../../messages/message'
import { NextPageWithLayout } from '../../common/common-types'
import PdImage from '../../components/pd-image/pd-image'
import { useChinaDetector } from '../../common/region-locale'
import FullPageLayout from '../../components/full-page-layout/full-page-layout'
import PdForm, { FieldItem } from '../../components/pd-atom-form/pd-atom-form'
import { useTranslations, useLocale } from 'next-intl'
import useSWR from 'swr'
import OSS from 'ali-oss'
import Link from 'next/link'
import PdUpload from '../../components/pd-upload/upload'
import { getOSSTokenAPI, getOSSToken } from '../../service/alioss.service'
import { withSEO } from '../../common/with-seo'
import toast from 'react-hot-toast'
import { useRouter } from 'next/router'
import { FieldValues } from 'react-hook-form'
import { FeedbackFormFields, submitFeedbackFormData } from '../../service/feedback.service'

const Feedback: NextPageWithLayout = () => {
  const t = useTranslations()
  const inChina = useChinaDetector()
  const locale = useLocale()
  const router = useRouter()
  const [agree, setAgree] = useState<boolean>(false)
  const [fileUrl, setFileUrl] = useState<string>()
  const [uploadFile, setUploadFile] = useState<File | undefined>()

  const privacyUrl = useMemo(() => {
    if (inChina) {
      return 'https://www.pudutech.com/privacy'
    } else {
      return 'https://www.pudurobotics.com/' + locale + '/privacy'
    }
  }, [inChina])

  useEffect(() => {
    if (!uploadFile) {
      setFileUrl(undefined)
    }
  }, [uploadFile])

  const FormFields: FieldItem[] = [
    {
      type: 'INPUT',
      label: 'Country/Region',
      fieldName: 'region',
      placeholder: 'Enter your country/region',
      rule: { required: true, maxLength: 180 },
    },
    {
      type: 'INPUT',
      label: 'Company Name',
      fieldName: 'companyName',
      placeholder: 'Enter your company name',
      rule: { required: true, maxLength: 180 },
    },
    {
      type: 'INPUT',
      label: 'Name',
      fieldName: 'name',
      placeholder: 'Enter your name',
      rule: { required: true, maxLength: 180 },
    },
    {
      type: 'INPUT',
      label: 'Email',
      fieldName: 'email',
      placeholder: 'Enter your email',
      rule: { required: true, maxLength: 180 },
    },
    {
      type: 'SELECT',
      label: 'What would you like to discuss?',
      fieldName: 'discuss',
      placeholder: 'Select your answer',
      rule: { required: true },
      options: [
        { label: 'Consultation on policy information', value: 'Consultation on policy information' },
        { label: 'Product optimization suggestions', value: 'Product optimization suggestions' },
        { label: 'Market information feedback', value: 'Market information feedback' },
        { label: 'Product quality complaints', value: 'Product quality complaints' },
        { label: 'Others', value: 'Others' },
      ],
    },
    {
      type: 'TEXT',
      label: 'Description of needs',
      fieldName: 'desc',
      rule: { maxLength: 600 },
      placeholder: 'Enter your description of needs',
    },
  ]

  async function uploadFileToOSS() {
    try {
      if (uploadFile && !fileUrl) {
        const fileType = uploadFile?.type?.split('/')[1]
        let filename = `${new Date().getTime()}.${fileType}`
        const ossconfig = await getOSSToken()
        if (ossconfig) {
          const client = new OSS({
            ...ossconfig,
            secure: true,
            timeout: 1000 * 60 * 10,
          })
          const result = await client.put(`official-website/feedback-form-file-test/${filename}`, uploadFile)
          setFileUrl(result.url)
          return result.url
        }
      }
    } catch (error) {
      console.log(error)
    }
  }

  async function handleSubmit(values: FieldValues) {
    if (agree) {
      uploadFileToOSS().then((url) => {
        const params: any = { ...values, attach: url || fileUrl }
        submitFeedbackFormData(params)
          .then((res: any) => {
            if (res.code === 200) {
              return router.push(
                { pathname: '/contact-us/success', query: { ...values, formId: 'feedback' } },
                '/contact-us/success' + `${values?.companyName ? '?companyName=' + values?.companyName : ''}`
              )
            } else {
              toast.error(res.message || 'Failed')
            }
          })
          .finally(() => {})
      })
    }
  }
  return (
    <div className="feedback w-full h-fit bg-white">
      <div className={['w-full bg-pdbg flex flex-col items-center h-450px', 'laptop:h-91'].join(' ')}>
        <div className={['h-full flex flex-col', 'laptop:w-980px laptop:flex-row laptop:justify-between'].join(' ')}>
          <div className={['flex flex-col p-6', 'laptop:w-150 laptop:p-0 laptop:pt-65px laptop:pb-107px'].join(' ')}>
            <div className={['w-78px h-6', 'laptop:w-98px laptop:h-30px'].join(' ')}>
              <PdImage src={'/feedback/logo.png'} enableMask={false} object="cover" width={'100%'} height={'100%'} />
            </div>
            <h2 className={['text-black/90 font-semibold text-28 mt-10', 'laptop:mt-12 laptop:text-42'].join(' ')}>
              Suggestion and Complain
            </h2>
            <p className={['text-black/60 text-14 mt-3', 'laptop:mt-4'].join(' ')}>
              We highly encourage you to share your thoughts and suggestions with us at any time. Pudu Robotics deeply
              values your ideas and feedback.
            </p>
          </div>
          <div className={['w-40 h-40 mx-auto', 'laptop:w-320px laptop:h-320px laptop:m-0'].join(' ')}>
            <PdImage src={'/feedback/banner.png'} enableMask={false} object="cover" width={'100%'} height={'100%'} />
          </div>
        </div>
      </div>
      <div className={['w-full bg-white flex flex-col items-center laptop:mb-30'].join(' ')}>
        <div
          className={[
            'w-full py-15 px-6',
            'laptop:w-980px laptop:bg-white laptop:px-143px laptop:py-80px laptop:-mt-41px laptop:shadow-[0_8px_40px_rgba(0,0,0,0.06)]',
          ].join(' ')}
        >
          <PdForm
            fields={FormFields}
            onSubmit={handleSubmit}
            bottomRender={(trigger, getValues) => {
              return (
                <div>
                  <PdUpload
                    onChange={setUploadFile}
                    label={'Attachment Upload (Optional)'}
                    extra={
                      'Please upload in .ppt, .pptx, .pdf, .xls, .xlsx, .docx, .doc, .png, .jpeg, .jpg, .dng, .mp4 formats. The total size should be less than 10 MB. If you need to upload more attachments to describe the issue better, we recommend you upload files to the network disk/cloud and provide the link in the Description of Needs (with a password for the encrypted link) so that we can resolve the issue for you as soon as possible.'
                    }
                  />
                  <div className="laptop:mt-8 mt-10">
                    <div className="flex flex-row gap-x-4">
                      <input
                        className="mt-1 !rounded-sm border border-[#C2C2C2] cursor-pointer"
                        name="agree"
                        type="checkbox"
                        onChange={(e: any) => {
                          setAgree(e?.target?.checked)
                        }}
                      />
                      <p className="text-sm text-black/60">
                        {
                          "Pudu Robotics is committed to protecting and respecting your privacy. By clicking the 'Submit', you confirm that you have read and agreed to Pudu Robotics "
                        }
                        <Link href={'/' + locale + '/privacy'}>
                          <a
                            className="text-[#0066FF] cursor-pointer"
                            href={privacyUrl}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {t('Privacy')}
                          </a>
                        </Link>
                        {' .'}
                      </p>
                    </div>
                    <div className="w-full text-center">
                      <input
                        className={[
                          'cursor-pointer laptop:mt-12 mt-6 laptop:w-auto w-full px-86px py-13px rounded-sm text-white text-20',
                          agree ? 'bg-[#0066FF]' : 'bg-[#C2DAFF]',
                        ].join(' ')}
                        disabled={!agree}
                        type="submit"
                        value="Submit"
                      />
                    </div>
                  </div>
                </div>
              )
            }}
          />
        </div>
      </div>
    </div>
  )
}
Feedback.getLayout = function (page: ReactElement, seo) {
  return (
    <FullPageLayout
      // pageHeight={'!h-full'}
      hideFooter={true}
      pageTitle={seo.title}
      pageDescription={seo.description}
      pageKeywrods={seo.keywords}
      ogtype={'website'}
    >
      {page}
    </FullPageLayout>
  )
}
export default Feedback

export const getStaticProps = withSEO(
  async ({ locale }: GetStaticPropsContext) => {
    const messages = retriveMessages(['feedback'], locale as LocaleType)
    return {
      props: {
        messages,
      },
    }
  },
  () => '/feedback'
)
